import React, { useMemo, useRef, useState } from 'react';
import { useBilling } from '../../contexts/BillingContext';
import { BillingAccount, SalesDocument } from '../../types/billing';
import { formatMoney, fromN, newClientKey, toN, todayLusaka } from '../../utils/billingMoney';
import { Dialog } from '../../components/ui/Dialog';
import { Button } from '../../components/ui/Button';
import { AccountPicker } from './AccountPicker';
type Props = {
  invoice?: SalesDocument;
  onClose: () => void;
  onDone: (message?: string, id?: string) => void;
};

/** Issue a credit note — against an invoice (reduces its balance) or as general account credit. */
export function CreditNoteModal({
  invoice,
  onClose,
  onDone
}: Props) {
  const {
    accounts,
    documents,
    issueCreditNote,
    config
  } = useBilling();
  const key = useRef(newClientKey());
  const fmt = (v: number) => formatMoney(v, config.regional.currencySymbol);
  const [account, setAccount] = useState<BillingAccount | null>(invoice ? accounts.find(a => a.id === invoice.accountId) ?? null : null);
  const [invoiceId, setInvoiceId] = useState(invoice?.id ?? '');
  const [amount, setAmount] = useState(invoice ? String(invoice.balance) : '');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(todayLusaka());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const invoices = useMemo(() => documents.filter(d => d.type === 'invoice' && d.state === 'Issued' && d.accountId === account?.id), [documents, account]);
  const selected = invoices.find(d => d.id === invoiceId);
  const creditable = selected ? fromN(toN(selected.total) - toN(selected.amountCredited)) : null;
  const submit = async () => {
    if (!account) return setError('Choose the account to credit.');
    setBusy(true);
    setError('');
    const result = await issueCreditNote({
      id: `cn-${key.current}`,
      account,
      invoiceId: invoiceId || undefined,
      amount: Number(amount),
      reason,
      date
    });
    setBusy(false);
    if (!result.ok) return setError(result.error ?? 'Could not issue the credit note.');
    onDone(result.message, result.id);
  };
  return <Dialog title="Issue credit note" subtitle="Credits are recorded, never deleted. Unapplied credit stays on the account." onClose={onClose} size="lg" footer={<>{error && <p role="alert" className="mr-auto text-sm text-red-700" data-id="element-3099" data-mp="39v5foia6tnnm">{error}</p>}<Button variant="outline" onClick={onClose} data-id="element-3100" data-mp="34vsltdkwfgbj">Cancel</Button><Button onClick={submit} isLoading={busy} data-id="element-3101" data-mp="5fm5xibh36mh">Issue credit note</Button></>} data-id="element-3098" data-mp="3duecxx4fs3nx">
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-3102" data-mp="1dhd2lsume0w">
        <div className="sm:col-span-2" data-id="element-3103" data-mp="1eslx015ibyv">{invoice ? <p className="text-sm" data-id="element-3104" data-mp="3ss6pqhilmdyf"><span className="text-slate-500" data-id="element-3105" data-mp="3srn5gu5h7dh8">Account:</span> <strong data-id="element-3106" data-mp="3sqc410zi6hta">{invoice.accountName}</strong></p> : <AccountPicker id="cn-account" label="Account" value={account} onChange={a => {
          setAccount(a);
          setInvoiceId('');
        }} allowCreate={false} required data-id="element-3107" data-mp="3ntbu2ezj8uww" />}</div>
        <div data-id="element-3108" data-mp="p56d6obww6se">
          <label htmlFor="cn-invoice" className="text-sm font-medium text-slate-700" data-id="element-3109" data-mp="p4wor56im7ot">Against invoice</label>
          <select id="cn-invoice" value={invoiceId} onChange={e => setInvoiceId(e.target.value)} disabled={Boolean(invoice)} className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-id="element-3110" data-mp="rnhwlit6275e">
            <option value="" data-id="element-3111" data-mp="11lxp9q7udoh0">None — general account credit</option>
            {invoices.map(d => <option key={d.id} value={d.id} data-id="element-3112" data-mp="15naqfmzzp3hl">{d.number} — balance {fmt(d.balance)}</option>)}
          </select>
          {creditable !== null && <p className="mt-1 text-xs text-slate-500" data-id="element-3113" data-mp="1alcd0hwmjkxt">Up to {fmt(creditable)} can still be credited on this invoice.</p>}
        </div>
        <div data-id="element-3114" data-mp="1fjmaiz5tmxml"><label htmlFor="cn-date" className="text-sm font-medium text-slate-700" data-id="element-3115" data-mp="1fjduwzibde7p">Date</label><input id="cn-date" type="date" value={date} onChange={e => setDate(e.target.value)} className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-id="element-3116" data-mp="1kcp3b52ry31q" /></div>
        <div data-id="element-3117" data-mp="1te6fwdgz3mlo"><label htmlFor="cn-amount" className="text-sm font-medium text-slate-700" data-id="element-3118" data-mp="1te6k6po0a8rh">Credit amount</label><input id="cn-amount" type="number" min="0.01" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums" data-id="element-3119" data-mp="1ycq46kj1hah7" /></div>
        <div className="sm:col-span-2" data-id="element-3120" data-mp="1gljmfx72efw8"><label htmlFor="cn-reason" className="text-sm font-medium text-slate-700" data-id="element-3121" data-mp="1ueretr2yzfez">Reason<span className="text-red-600" data-id="element-3122" data-mp="1ugvge2dec2c3"> *</span></label><textarea id="cn-reason" rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Test cancelled after billing" className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-id="element-3123" data-mp="1pgzce34p8awj" /></div>
      </div>
    </Dialog>;
}